import { Clipboard } from '@angular/cdk/clipboard';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment-timezone';
import Swal from 'sweetalert2';
import { S3UrlService } from 'src/app/shared/services/s3-url.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import {
  KycStatus,
  OnboardResponseDto,
  RevalidateKycResponse,
  UserWithWallet,
  WalletStatus,
} from '../../../interfaces/binance-wallet.interface';
import { BinanceWalletAdminService } from '../../../services/binance-wallet-admin.service';

export interface CreateWalletModalData {
  user: UserWithWallet;
}

export interface CreateWalletModalResult {
  /** `true` si hubo algún cambio (onboard ejecutado o wallet creada) y el listado debe refrescar. */
  refreshNeeded: boolean;
}

type OnboardState =
  | 'IDLE'
  | 'LOADING'
  | 'DONE'
  | 'WAITING_KYC'
  | 'ERROR'
  | 'KYC_REJECTED';

type WalletState =
  | 'DISABLED_NO_SUB'
  | 'DISABLED_NO_KYC'
  | 'IDLE'
  | 'LOADING'
  | 'DONE_CACHED'
  | 'DONE_FRESH'
  | 'ERROR';

type PrevalidationState =
  | 'IDLE'         // estado inicial — el operador todavía no clickeó "Revalidar"
  | 'LOADING'      // request en vuelo
  | 'OK'           // face match Y ocr OK
  | 'PARTIAL'      // ocr PARTIAL / FAIL pero face match
  | 'FAIL'         // face NO match (lo más grave)
  | 'BLOCKED'      // faltan fotos requeridas — no se puede revalidar
  | 'ERROR';       // 400/404/502 del backend KYC

export interface KycChecklistPhoto {
  label: string;
  url: string | null;
  required: boolean;
}

export interface KycChecklist {
  ok: { label: string; value: string }[];
  missing: string[];
  missingOptional: string[];
  photos: KycChecklistPhoto[];
  totalMissing: number;
  isReady: boolean;
}

/**
 * Modal "Crear Wallet" rediseñado según `FRONTEND_WALLET_MODAL_BRIEF.md`.
 *
 * Separa el flujo en dos botones explícitos:
 *   1. Registrar Subcuenta (POST /onboard) — crea subcuenta + envía KYC.
 *   2. Crear Wallet (POST /:walletId/wallet-trc20) — genera deposit address.
 *
 * Al abrir, refresca el status con `listUsersWithWallet({ usuarioId })`.
 * Si la respuesta trae `walletTrc20` populado, el Paso 2 abre directo
 * en `DONE_CACHED` sin invocar el POST (cambio backend confirmado).
 *
 * Si tras onboard `expectsWebhook = true`, arranca polling con backoff:
 *   - 0-5 min:   cada 30s
 *   - 5-60 min:  cada 5 min
 *   - >60 min:   se detiene, operador refresca manual
 *
 * Limpia el timer en `ngOnDestroy` para evitar leaks si se cierra el modal.
 */
@Component({
  selector: 'app-create-wallet-modal',
  templateUrl: './create-wallet-modal.component.html',
  styleUrls: ['./create-wallet-modal.component.sass'],
})
export class CreateWalletModalComponent implements OnInit, OnDestroy {
  user: UserWithWallet;
  refreshNeeded = false;

  onboardState: OnboardState = 'IDLE';
  walletState: WalletState = 'DISABLED_NO_SUB';

  onboardMessage: string | null = null;
  onboardMessageColor: 'info' | 'success' | 'warning' | 'danger' | 'orange' =
    'info';
  walletMessage: string | null = null;

  trc20Address: string | null = null;
  trc20FetchedAt: string | null = null;

  // ── KYC Checklist (campos textuales + fotos firmadas S3) ──────
  kycChecklist: KycChecklist | null = null;

  // ── Prevalidación KYC interna Aguacate ─────────────────────────
  prevalidationState: PrevalidationState = 'IDLE';
  /** Resultado fresco de `POST /users/:id/revalidate-kyc` (incluye `fromCache`). */
  prevalidationResult: RevalidateKycResponse | null = null;
  /** Mensaje de error tipado (faltan campos, OCR ilegible, etc.). */
  prevalidationError: string | null = null;

  private pollTimer: any = null;
  private pollStartedAt: number = 0;
  private destroyed = false;

  constructor(
    public dialogRef: MatDialogRef<
      CreateWalletModalComponent,
      CreateWalletModalResult
    >,
    @Inject(MAT_DIALOG_DATA) private data: CreateWalletModalData,
    private walletAdmin: BinanceWalletAdminService,
    private spinner: SpinnerService,
    private clipboard: Clipboard,
    private s3UrlService: S3UrlService,
  ) {
    this.user = data.user;
  }

  async ngOnInit(): Promise<void> {
    // No hacemos refresh contra el backend al abrir: la fila `user`
    // ya viene fresca desde el listado (incluye `walletTrc20` cuando
    // existe). Hacer un `listUsersWithWallet({ usuarioId })` aquí
    // disparaba bug de race: si el backend devuelve otro usuario (o
    // si el filtro `usuarioId` no se aplica), la respuesta tardía
    // sobreescribe al usuario correcto y la UI muestra data ajena.
    // Todo el render del modal sale de `this.user` directamente.
    this.kycChecklist = await this.buildKycChecklist(this.user);
    this.applyStatus(this.user);
    // Bloqueo defensivo: si faltan fotos requeridas, no se le puede
    // pegar al KYC backend. Marca el card como BLOCKED y evita el call.
    this.computePrevalidationBlockedIfNeeded();
    // Nada de auto-revalidación ni de preload: el modal arranca en
    // IDLE y el operador dispara la prevalidación con el botón
    // "Revalidar ahora" sólo cuando la quiere ver. Cada veredicto
    // pertenece sin ambigüedad al usuario abierto en pantalla.
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopPolling();
  }

  // ============================================================
  // Refresh status (open + polling tick + manual refresh)
  // ============================================================
  async refreshStatus(showSpinner: boolean = true): Promise<void> {
    const ref = showSpinner ? this.spinner.start() : null;
    // Capturamos el target ANTES de la request para detectar respuestas
    // tardías que pertenecen a otro modal/usuario (componente reciclado,
    // backend que ignora `usuarioId`, etc.).
    const targetUsuarioId = this.user.usuarioId;
    try {
      const response = await this.walletAdmin.listUsersWithWallet({
        usuarioId: targetUsuarioId,
        page: 1,
        limit: 1,
      });
      // Si el componente se cerró o el usuario del modal cambió mientras
      // viajaba la response, descartamos sin tocar nada.
      if (this.destroyed || this.user.usuarioId !== targetUsuarioId) return;
      const fresh = response.data?.[0];
      // Guardia defensiva: si el backend nos devuelve OTRO usuario
      // (filtro `usuarioId` no soportado, listado paginado por defecto,
      // etc.), no overrideamos al usuario correcto. Sólo aplicamos si el
      // payload coincide con el target.
      if (fresh && fresh.usuarioId === targetUsuarioId) {
        this.user = fresh;
        this.applyStatus(fresh);
        this.kycChecklist = await this.buildKycChecklist(fresh);
      }
    } catch (error) {
      // Silencioso en polling, vocal en refresh manual
      if (showSpinner) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo refrescar el estado del usuario.',
          confirmButtonColor: '#1e5a3f',
        });
      }
    } finally {
      if (ref !== null) this.spinner.stop(ref);
    }
  }

  /** Recalcula los dos states a partir de la última `UserWithWallet`. */
  private applyStatus(user: UserWithWallet): void {
    // Paso 2 — address cacheada en el GET
    if (user.walletTrc20) {
      this.trc20Address = user.walletTrc20.address;
      this.trc20FetchedAt = user.walletTrc20.fetchedAt;
      this.walletState = 'DONE_CACHED';
    } else if (!user.walletId || user.walletStatus !== 'ACTIVE') {
      this.walletState = 'DISABLED_NO_SUB';
    } else if (user.kycStatus !== 'PASS') {
      this.walletState = 'DISABLED_NO_KYC';
    } else {
      // ACTIVE + PASS + sin address → listo para crear
      if (this.walletState !== 'LOADING' && this.walletState !== 'DONE_FRESH') {
        this.walletState = 'IDLE';
      }
    }

    // Paso 1 — onboard
    if (this.onboardState === 'LOADING') return; // no pisar request en vuelo

    if (!user.walletId) {
      this.onboardState = 'IDLE';
      return;
    }

    if (user.walletStatus === 'ERROR') {
      this.onboardState = 'ERROR';
      return;
    }

    if (user.kycStatus === 'REJECT') {
      this.onboardState = 'KYC_REJECTED';
      return;
    }

    if (user.kycStatus === 'PENDING') {
      this.onboardState = 'WAITING_KYC';
      return;
    }

    // walletId existe, no está en error, no está rechazado, no está pending
    this.onboardState = 'DONE';
  }

  // ============================================================
  // Paso 1 — Registrar Subcuenta + KYC
  // ============================================================
  get canSubmitOnboard(): boolean {
    return (
      this.onboardState === 'IDLE' ||
      this.onboardState === 'ERROR' ||
      this.onboardState === 'KYC_REJECTED'
    );
  }

  async registrarSubcuenta(): Promise<void> {
    if (!this.canSubmitOnboard) return;
    this.onboardState = 'LOADING';
    this.onboardMessage = null;

    const ref = this.spinner.start();
    try {
      const response = await this.walletAdmin.onboardUser(this.user.usuarioId);
      this.spinner.stop(ref);
      this.refreshNeeded = true;
      this.handleOnboardResponse(response);
    } catch (error: any) {
      this.spinner.stop(ref);
      this.handleOnboardError(error);
    }
  }

  private handleOnboardResponse(response: OnboardResponseDto): void {
    // Mensaje listo desde backend + color según el cruce de acciones
    this.onboardMessage = response.message;
    this.onboardMessageColor = this.colorForKycAction(response.kycAction);

    // Refrescar status del usuario y arrancar polling si toca
    this.refreshStatus(false);

    if (response.expectsWebhook) {
      this.startPolling();
    }
  }

  private handleOnboardError(error: any): void {
    const status = error?.status;
    const body = error?.error ?? {};
    const apiMsg: string = body?.message ?? 'Error al registrar subcuenta';

    let title = 'Error';
    let html = apiMsg;

    if (status === 400 && body.estadoComplianceId !== undefined) {
      title = 'Usuario no aprobado';
      html =
        'Este usuario aún no fue aprobado por compliance interno. Primero debe completarse el KYC interno de Aguacate.';
    } else if (status === 400 && Array.isArray(body.missing)) {
      title = 'Faltan datos del perfil';
      const items = body.missing
        .map((m: string) => `<li>${m}</li>`)
        .join('');
      html = `<p style="margin: 0 0 8px 0;">Faltan los siguientes campos en el perfil del usuario:</p><ul style="text-align: left; margin: 0; padding-left: 20px;">${items}</ul>`;
    } else if (status === 404) {
      title = 'Usuario no encontrado';
      html = 'Usuario no existe.';
    } else if (status === 409 && body.kycSaasCode) {
      title = 'Binance rechazó el envío';
      html = `Código: <strong>${body.kycSaasCode}</strong><br>${
        body.kycSaasMessage ?? apiMsg
      }`;
    } else if (status === 502) {
      title = 'Error temporal con Binance';
      html = 'Error temporal con Binance. Intenta de nuevo en unos minutos.';
    } else if (status === 500) {
      title = 'Error interno';
      html = 'Error interno. Contactar a soporte.';
    }

    this.onboardState = status === 409 && body.kycSaasCode ? 'KYC_REJECTED' : 'ERROR';
    this.onboardMessage = null;

    Swal.fire({
      icon: 'error',
      title,
      html,
      confirmButtonColor: '#1e5a3f',
    });
  }

  private colorForKycAction(
    action: OnboardResponseDto['kycAction'],
  ): 'info' | 'success' | 'warning' | 'orange' | 'danger' {
    switch (action) {
      case 'SUBMITTED':
        return 'info';
      case 'SKIPPED_APPROVED':
        return 'success';
      case 'SKIPPED_PENDING':
        return 'warning';
      case 'SKIPPED_RFI':
        return 'orange';
      case 'SKIPPED_UNKNOWN':
      default:
        return 'danger';
    }
  }

  // ============================================================
  // Paso 2 — Crear Wallet TRC20
  // ============================================================
  get canCreateWallet(): boolean {
    return (
      this.user.walletStatus === 'ACTIVE' &&
      this.user.kycStatus === 'PASS' &&
      !this.trc20Address &&
      this.walletState !== 'LOADING'
    );
  }

  get isWalletDone(): boolean {
    return this.walletState === 'DONE_CACHED' || this.walletState === 'DONE_FRESH';
  }

  async crearWallet(): Promise<void> {
    if (!this.canCreateWallet || !this.user.walletId) return;

    this.walletState = 'LOADING';
    this.walletMessage = null;

    const ref = this.spinner.start();
    try {
      const response = await this.walletAdmin.createTrc20Wallet(this.user.walletId);
      this.spinner.stop(ref);
      this.trc20Address = response.address;
      this.trc20FetchedAt = response.fetchedAt;
      this.walletState = response.source === 'CACHE' ? 'DONE_CACHED' : 'DONE_FRESH';
      this.refreshNeeded = true;
    } catch (error: any) {
      this.spinner.stop(ref);
      this.handleWalletError(error);
    }
  }

  private handleWalletError(error: any): void {
    const status = error?.status;
    const body = error?.error ?? {};
    const apiMsg: string = body?.message ?? 'Error al crear la wallet';

    let title = 'Error';
    let html = apiMsg;

    if (status === 404) {
      title = 'Wallet no encontrada';
      html = 'La wallet no existe. Reintenta el paso 1.';
    } else if (status === 409 && body.kycStatus !== undefined) {
      title = 'KYC pendiente';
      html =
        'El usuario se encuentra pendiente de aprobación de KYC por Binance.';
      // Race condition: refrescar status para sincronizar UI
      this.refreshStatus(false);
    } else if (status === 409 && body.walletStatus !== undefined) {
      title = 'Wallet no operativa';
      html = `La wallet no está operativa (${body.walletStatus}). Revisar paso 1.`;
    } else if (status === 502) {
      title = 'Error temporal con Binance';
      html = 'Error temporal con Binance. Intenta de nuevo.';
    }

    this.walletState = 'ERROR';
    this.walletMessage = null;

    Swal.fire({
      icon: status === 409 ? 'warning' : 'error',
      title,
      html,
      confirmButtonColor: '#1e5a3f',
    });
  }

  // ============================================================
  // Polling (brief §6)
  // ============================================================
  private startPolling(): void {
    this.stopPolling();
    this.pollStartedAt = Date.now();
    this.schedulePollTick();
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private schedulePollTick(): void {
    if (this.destroyed) return;
    const elapsedMin = (Date.now() - this.pollStartedAt) / 60000;

    if (elapsedMin >= 60) {
      // Detener: operador refresca manual
      this.stopPolling();
      return;
    }

    const nextDelayMs = elapsedMin < 5 ? 30_000 : 5 * 60_000;
    this.pollTimer = setTimeout(async () => {
      if (this.destroyed) return;
      await this.refreshStatus(false);

      // Cancelar si el KYC ya tiene un veredicto
      const ks = this.user.kycStatus;
      if (
        ks === 'PASS' ||
        ks === 'REJECT' ||
        ks === 'RFI_REQUIRED' ||
        ks === 'UNKNOWN'
      ) {
        this.stopPolling();
        return;
      }

      this.schedulePollTick();
    }, nextDelayMs);
  }

  // ============================================================
  // UI helpers
  // ============================================================
  copyAddress(): void {
    if (!this.trc20Address) return;
    this.clipboard.copy(this.trc20Address);
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: 'Dirección copiada al portapapeles',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
    });
  }

  walletStatusLabel(s: WalletStatus | null): string {
    switch (s) {
      case 'CREATING':
        return 'Creando subcuenta...';
      case 'KYC_PENDING':
        return 'Subcuenta lista, KYC pendiente';
      case 'ACTIVE':
        return 'Subcuenta activa';
      case 'BLOCKED':
        return 'Subcuenta deshabilitada';
      case 'ERROR':
        return 'Error en creación';
      case null:
      default:
        return 'Sin subcuenta';
    }
  }

  walletStatusColor(s: WalletStatus | null): string {
    switch (s) {
      case 'ACTIVE':
        return '#15803d';
      case 'KYC_PENDING':
        return '#a16207';
      case 'CREATING':
        return '#6b7280';
      case 'BLOCKED':
        return '#b91c1c';
      case 'ERROR':
        return '#dc2626';
      default:
        return '#9ca3af';
    }
  }

  kycStatusLabel(s: KycStatus | null): string {
    switch (s) {
      case 'NONE':
        return 'Sin enviar';
      case 'PENDING':
        return 'En revisión por Binance';
      case 'PASS':
        return 'Aprobado';
      case 'REJECT':
        return 'Rechazado';
      case 'RFI_REQUIRED':
        return 'Binance solicita info adicional';
      case 'UNKNOWN':
        return 'Estado desconocido — revisar';
      default:
        return 'Sin estado';
    }
  }

  kycStatusColor(s: KycStatus | null): string {
    switch (s) {
      case 'PASS':
        return '#15803d';
      case 'PENDING':
        return '#a16207';
      case 'REJECT':
        return '#b91c1c';
      case 'RFI_REQUIRED':
        return '#c2410c';
      case 'UNKNOWN':
        return '#b91c1c';
      case 'NONE':
      default:
        return '#6b7280';
    }
  }

  onboardMessageBgColor(): string {
    switch (this.onboardMessageColor) {
      case 'success':
        return '#dcfce7';
      case 'warning':
        return '#fef9c3';
      case 'orange':
        return '#ffedd5';
      case 'danger':
        return '#fee2e2';
      case 'info':
      default:
        return '#dbeafe';
    }
  }

  onboardMessageTextColor(): string {
    switch (this.onboardMessageColor) {
      case 'success':
        return '#166534';
      case 'warning':
        return '#854d0e';
      case 'orange':
        return '#9a3412';
      case 'danger':
        return '#991b1b';
      case 'info':
      default:
        return '#1e40af';
    }
  }

  userDisplayName(): string {
    return `${this.user.nombre ?? ''} ${this.user.apellidos ?? ''}`.trim() || `Usuario #${this.user.usuarioId}`;
  }

  userDisplayId(): string {
    if (this.user.tipoUsuarioId === 1 && this.user.rutEmpresa) {
      return `RUT empresa ${this.user.rutEmpresa}`;
    }
    return `${this.user.tipoId ?? ''} ${this.user.numeroId ?? ''}`.trim();
  }

  // ============================================================
  // Prevalidación KYC interna (Aguacate KYC SaaS — OCR + Rekognition)
  // ============================================================
  /**
   * El modal NO consume `user.prevalidation` ni llama a
   * `getLatestKycValidation()`. El veredicto se produce únicamente con
   * `revalidatePrevalidation()`, disparado por el botón "Revalidar". Así
   * cada resultado pertenece sin ambigüedad al `usuarioId` que está
   * actualmente abierto en pantalla.
   */

  /** Hay un resultado fresco corrido en esta sesión del modal. */
  get hasFreshPrevalidation(): boolean {
    return this.prevalidationResult !== null;
  }

  /** Texto del veredicto actual — siempre derivado del run fresco. */
  prevalidationBadgeLabel(): string {
    switch (this.prevalidationState) {
      case 'OK':
        return 'Prevalidación OK';
      case 'PARTIAL':
        return 'OCR parcial — revisar';
      case 'FAIL':
        return 'Face NO match';
      case 'BLOCKED':
        return 'Faltan fotos';
      case 'ERROR':
        return 'Error al validar';
      case 'LOADING':
        return 'Revalidando...';
      case 'IDLE':
      default:
        return 'Sin prevalidar';
    }
  }

  prevalidationBadgeColor(): { bg: string; fg: string } {
    switch (this.prevalidationState) {
      case 'OK':
        return { bg: '#dcfce7', fg: '#15803d' };
      case 'PARTIAL':
        return { bg: '#fef9c3', fg: '#a16207' };
      case 'FAIL':
      case 'ERROR':
        return { bg: '#fee2e2', fg: '#b91c1c' };
      case 'BLOCKED':
        return { bg: '#fee2e2', fg: '#991b1b' };
      case 'LOADING':
        return { bg: '#dbeafe', fg: '#1e40af' };
      case 'IDLE':
      default:
        return { bg: '#f3f4f6', fg: '#6b7280' };
    }
  }

  /** Falta foto frontal o selfie → no se puede pegarle al KYC backend. */
  private computePrevalidationBlockedIfNeeded(): void {
    const frontal = (this.user.fotoIdfrontal ?? '').trim();
    const selfie = (this.user.selfie ?? '').trim();
    if (!frontal || !selfie) {
      this.prevalidationState = 'BLOCKED';
      this.prevalidationError =
        !frontal && !selfie
          ? 'Faltan foto frontal y selfie en el perfil del usuario.'
          : !frontal
            ? 'Falta foto frontal del documento.'
            : 'Falta selfie.';
    }
  }

  get canRevalidate(): boolean {
    return (
      this.prevalidationState !== 'LOADING' &&
      this.prevalidationState !== 'BLOCKED'
    );
  }

  /** Corre revalidación fresca contra el KYC backend. */
  async revalidatePrevalidation(): Promise<void> {
    if (!this.canRevalidate) return;
    this.prevalidationState = 'LOADING';
    this.prevalidationError = null;

    const ref = this.spinner.start();
    try {
      const result = await this.walletAdmin.revalidateKyc(this.user.usuarioId);
      this.spinner.stop(ref);
      // Si el modal se cerró durante el OCR (5-15s), no mutamos state.
      if (this.destroyed) return;
      this.applyPrevalidationResult(result);
      this.refreshNeeded = true;
    } catch (error: any) {
      this.spinner.stop(ref);
      if (this.destroyed) return;
      this.handlePrevalidationError(error);
    }
  }

  private applyPrevalidationResult(result: RevalidateKycResponse): void {
    this.prevalidationResult = result;
    const faceOk = !!result.face?.isMatch;
    const ocrOverall = result.ocr?.overall;

    if (!faceOk) {
      this.prevalidationState = 'FAIL';
    } else if (ocrOverall === 'OK') {
      this.prevalidationState = 'OK';
    } else {
      // face match pero ocr PARTIAL/FAIL
      this.prevalidationState = 'PARTIAL';
    }
  }

  private handlePrevalidationError(error: any): void {
    this.prevalidationState = 'ERROR';
    const status = error?.status;
    const apiMsg = error?.error?.message;
    let title = 'Error de revalidación';
    let text = apiMsg || 'No se pudo revalidar al usuario.';

    if (status === 400) {
      title = 'Datos del usuario insuficientes';
      text =
        apiMsg ||
        'Al usuario le faltan campos requeridos (cognitoId, foto frontal o selfie).';
    } else if (status === 404) {
      title = 'Usuario no encontrado';
      text = 'No existe el usuario en BD.';
    } else if (status === 502) {
      title = 'KYC backend no respondió';
      text = 'El servicio KYC devolvió un payload inválido. Reintenta en unos minutos.';
    }

    this.prevalidationError = text;
    Swal.fire({
      icon: 'error',
      title,
      text,
      confirmButtonColor: '#1e5a3f',
    });
  }

  /** ISO date del run fresco — `null` hasta que termina la revalidación. */
  get prevalidationDate(): string | null {
    return this.prevalidationResult?.validatedAt ?? null;
  }

  get prevalidationFromCache(): boolean {
    return !!this.prevalidationResult?.fromCache;
  }

  /** Resumen face match del run fresco. `—` si todavía no hay resultado. */
  get prevalidationFaceLabel(): string {
    if (!this.prevalidationResult) return '—';
    const f = this.prevalidationResult.face;
    const sim = typeof f.similarity === 'number' ? f.similarity.toFixed(1) : '—';
    return f.isMatch
      ? `Match (${sim}% ≥ umbral ${f.threshold}%)`
      : `No match (${sim}% < umbral ${f.threshold}%)`;
  }

  /** Resumen OCR del run fresco. `—` si todavía no hay resultado. */
  get prevalidationOcrLabel(): string {
    if (!this.prevalidationResult) return '—';
    const o = this.prevalidationResult.ocr;
    const conf =
      typeof o.confidence === 'number' ? ` · conf ${o.confidence.toFixed(1)}%` : '';
    const exp = o.documentExpired ? ' · documento vencido' : '';
    return `${o.overall}${conf}${exp}`;
  }

  // ============================================================
  // KYC Checklist (campos + fotos firmadas S3)
  // ============================================================
  /**
   * Firma una S3 key del User (`fotoIdfrontal`, `fotoIdPosterior`, `selfie`)
   * vía `S3UrlService` y devuelve una URL pre-signed (TTL 1h) para el
   * `<img>`. Post-VULN-01: el browser ya no firma — pide la URL al
   * backend. Por eso ahora retorna `Promise<string | null>`.
   */
  private signKycPhoto(
    key: string | null | undefined,
  ): Promise<string | null> {
    return this.s3UrlService.getReadUrl(key);
  }

  /** `YYYY-MM-DD` para `fechaNacimiento` / `fechaExp`. Vacío si no parsea. */
  private formatKycDate(iso: string | null): string {
    if (!iso) return '';
    const d = moment(new Date(iso));
    return d.isValid() ? d.format('YYYY-MM-DD') : '';
  }

  /**
   * Split OK/falta de los datos KYC que Binance pide en `share-kyc-data`.
   *  - `ok`: textuales cargados (required u opcional).
   *  - `missing`: textuales REQUERIDOS faltantes (rojo, bloquean Binance).
   *  - `missingOptional`: opcionales faltantes (gris, informativos).
   *  - `photos`: array de 3 (frontal, dorso, selfie) con URL firmada o
   *    `null` y flag `required`.
   *  - `isReady`: todos los requeridos cargados (texto + fotos req).
   */
  private async buildKycChecklist(user: UserWithWallet): Promise<KycChecklist> {
    const has = (v: string | null | undefined): boolean =>
      typeof v === 'string' && v.trim().length > 0;

    const items: {
      label: string;
      raw: string | null;
      display: string;
      required: boolean;
    }[] = [
      { label: 'Nombre',          raw: user.nombre,           display: user.nombre,                              required: true },
      { label: 'Apellidos',       raw: user.apellidos,        display: user.apellidos,                           required: true },
      { label: 'Nacimiento',      raw: user.fechaNacimiento,  display: this.formatKycDate(user.fechaNacimiento), required: true },
      { label: 'Nacionalidad',    raw: user.nacionalidad,     display: user.nacionalidad,                        required: true },
      { label: 'País residencia', raw: user.paisResidencia,   display: user.paisResidencia,                      required: true },
      { label: 'Ciudad',          raw: user.ciudadResidencia, display: user.ciudadResidencia,                    required: true },
      { label: 'Dirección',       raw: user.direccion,        display: '✓',                                      required: true },
      { label: 'RUT',             raw: user.numeroId,         display: user.numeroId,                            required: true },
      // Opcionales según spec Binance — no bloquean.
      { label: 'Sexo',            raw: user.sexo,             display: user.sexo,                                required: false },
      { label: 'Fecha exp. doc.', raw: user.fechaExp,         display: this.formatKycDate(user.fechaExp),        required: false },
    ];

    const ok: { label: string; value: string }[] = [];
    const missing: string[] = [];
    const missingOptional: string[] = [];
    for (const it of items) {
      if (has(it.raw)) ok.push({ label: it.label, value: it.display });
      else if (it.required) missing.push(it.label);
      else missingOptional.push(it.label);
    }

    const photoSpecs: { label: string; key: string | null; required: boolean }[] = [
      { label: 'Foto ID frontal', key: user.fotoIdfrontal,   required: true },
      { label: 'Foto ID dorso',   key: user.fotoIdPosterior, required: false },
      { label: 'Selfie',          key: user.selfie,          required: true },
    ];
    const photoUrls = await Promise.all(
      photoSpecs.map((p) => this.signKycPhoto(p.key)),
    );
    const photos: KycChecklistPhoto[] = photoSpecs.map((p, i) => ({
      label: p.label,
      url: photoUrls[i],
      required: p.required,
    }));
    const missingPhotosRequired = photos.filter((p) => !p.url && p.required).length;
    for (const p of photos) {
      if (!p.url && !p.required) missingOptional.push(p.label);
    }

    const totalMissing = missing.length + missingPhotosRequired;
    const isReady = totalMissing === 0;

    return { ok, missing, missingOptional, photos, totalMissing, isReady };
  }

  /** Handler `onerror` para `<img>` con URL firmada que falló al cargar. */
  onPhotoError(event: Event): void {
    const target = event.target as HTMLImageElement;
    if (target) {
      target.style.display = 'none';
      const next = target.nextElementSibling as HTMLElement | null;
      if (next) next.style.display = 'flex';
    }
  }

  close(): void {
    this.dialogRef.close({ refreshNeeded: this.refreshNeeded });
  }
}
